import "oc-ui/errorHandling.js";
import "./AppHandlebarsExtensions.js";
import Commons from "oc-commons/Commons.js";
import Cookies from "oc-commons/Cookies.js";
import DefaultAppSettings from "./DefaultAppSettings.js";
import DeviceDetection from "oc-commons/DeviceDetection.js";
import Dialogs from "oc-ui/dialogs/Dialogs";
import HBS from "./hbs/HBS.js";
import I18N from "oc-I18N";
import ListenerMixin from "oc-commons/ListenerMixin.js";
import localSettings from "oc-ui/localSettings.js";
import LZString from "lz-string";
import Mimetypes from "oc-commons/Mimetypes";
import UndoHandler from "oc-app/UndoHandler.js";
import UrlFluid from "oc-commons/UrlFluid";
import Urls from "oc-commons/Urls";
import Wkfg2BackendApi from "oc-app/Wkfg2BackendApi";
import Wkfg2TypeConverter from "oc-app/Wkfg2TypeConverter";
// import "@webcomponents/webcomponentsjs/webcomponents-bundle";
// import "@webcomponents/webcomponentsjs";

// AbstractApp darf keine Referenz auf oc-threejs haben, weil es Anwendungen gibt, die kein THREE verwenden!
//import "oc-ar/BufferGeometryToIndexed";

import { defaultCatchAsWarning } from "oc-ui/errorHandling";
import ModelsHelper from "./ModelsHelper";

Debugs.checkBabelSetSpread();

/**
 * Basis-Klasse für alle Holodeck-Apps.
 * Registriert sich als globales Object APP im Window-Scope.
 * APP wird in Holodeck als globaler Zugriffspunkt für die hier deklarierten Eigenschaften benutzt.
 *
 * Die App sollte folgende Events auf sich selbst auslösen:
 * - **changedConfiguration** immer nachdem die Configuration verändert wurde
 * - **ready** sobald alle initialen Ladevorgänge abgeschlossen sind und der Loading-Dialog ausgeblendet wurde
 *
 * ## Asynchrones Laden
 *
 * Der Ladevorgang einer App ist asynchron. Hier ein typisches Beispiel, wie man
 * seine Modelle und Konfigurationen asynchron lädt:
 *
 * ```
 * // Lädt die Materialien asynchron
 * let loadedMaterials = false;
 * UniversalLoader.loadMTL(modelsPath+'ZAUNPLANER.mtl', (materials)=>{
 *    UniversalLoader.setMaterialFactory(materials);
 *    loadedMaterials = true;
 * });
 *
 * // Lädt die Produkte asynchron
 * this.loadMetadata();
 *
 * Commons.delayUntil(()=>{
 *    // erst jetzt kann man die Konfiguration (und damit 3D-Modelle) laden
 *    this.loadInitialConfiguration();
 * },()=>loadedMaterials && APP.metadata.length>0);
 *
 * this.on('loadedInitialConfiguration', ()=> {
 *    UniversalLoader.delayUntilEverythingLoaded(()=>{
 *       // Blendet den Loading-Screen aus, sobald alles geladen ist
 *       this.hideInitialLoadingDialog();
 *    },'hideInitialLoadingDialog');
 * });
 * ```
 *
 */
export default class AbstractApp extends ListenerMixin() {
    /**
     * name der App
     * @type {string}
     */
    name;

    /**
     * das Wurzel-DOM-Element des Konfigurators
     * @type {*}
     */
    container;

    /**
     * ermöglicht Browser-lokales Überschreiben der Settings
     * @type {Object}
     */
    settingsHandler;

    /**
     * die Einstellparameter der App
     * @type {Object}
     */
    settings;

    /**
     * stellt undo / redo von Configurationsänderungen bereit
     * @type {UndoHandler}
     */
    undoHandler;

    /**
     * die Produkt-Metadaten, kann die Produktliste sein (Planer) oder ein Produkttree (3dkfg)
     * @type {Array<Object>}
     */
    metadata = [];

    /**
     * die Liste aller Configurationen, die dem User potentiell angezeigt werden
     * @type {Array<Object>}
     */
    configurationList = [];

    /**
     * die gerade angezeigte Konfiguration
     * @type {?Object}
     */
    configuration = null;

    /**
     * map der canvas fabric referenzen für den DruckDesigner
     * @type {?Object}
     */
    printDesignerMap;

    /**
     * der User, kann null sein
     * @type {?Object}
     */
    user = null;

    // ist die App im ready-State?
    ready = false;
    metamodel;


    /**
     * @param name interner, eindeutiger Bezeichner der App, wird nicht angezeigt
     * @param container JQuery-Element, in dem der Configurator erstellt wird
     * @param settings Einstellparameter
     */
    constructor(name, container = "body", settings = null) {
        super();
        this.name = name;
        this.container = $(container);
        this._appInitialize(settings);
    }

    // TODO: @private Crashes Typescript compiler, WTF?
    // /**
    //  * @param settings
    //  * @private
    //  */
    _appInitialize(settings) {
        this._appInitializeFromAbstractApp(settings);
    }

    // TODO: @private Crashes Typescript compiler, WTF?
    // /**
    //  * @param settings
    //  * @private
    //  */
    _appInitializeFromAbstractApp(settings) {
        this.version = '<<<<<---RELEASE_NUMBER--->>>>>';

        if (this.container.length == 0) {
            throw new Error("App constructed without container element");
        }

        // Convenience. Könnte bei Namenskollisionen mit umgebender Seite mittels string-replace-webpack-plugin ersetzt werden.
        window["APP"] = this;

        if (settings && settings.i18n_json) {
            I18N.setKeyValueMap(settings.i18n_json);
        }

        this.mergeSettings(settings);

        $.ajaxSetup({
            headers: this.getAjaxHeaders(),
        });

        this.on("ready", () => {
            this.ready = true;
            $("body").addClass("holoAppReady");
        });

        this.checkWebGL();

        this.undoHandler = this.createUndoHandler();

        if (this.settings.autosaveAndRestore) {
            this.setAutosaveAndRestore();
        }

        this.wkfgBackendApi = this.createWkfgBackendApi();
    }

    /** */
    createUndoHandler() {
        return new UndoHandler();
    }

    /** */
    getAjaxHeaders() {
        return {
            'X-CSRF-Token': this.settings.xcsrfToken,
            'wkfg-tenant': this.settings.tenant_key
        };
    }

    /** */
    mergeSettings(settings) {
        this.settingsHandler = localSettings(name + "_settings", settings);
        this.settings = Commons.defaultsDeepSafe(
            {},
            this.settingsHandler.getMerge(),
            DefaultAppSettings
        );
    }

    /** */
    checkWebGL() {
        if (typeof THREE !== "undefined") {
            if (!DeviceDetection.isWebGLAvailable()) {
                this.noWebGL();
                return false;
            }
        }
    }

    /** */
    noWebGL() {
        alert(I18N("Auf Ihrem System steht leider kein WebGL zur Verfügung."));
    }

    /**
     * Lädt die Configuration in die Stage und führt ein Reset des Arrangement aus
     */
    setStageFromConfiguration(configurationJson = null) {}

    /**
     * Updatet die Konfiguration wobei sich das Json nicht geändert hat. Eine Konfiguration ist ja eine
     * Zeile in der Konfigurationstabelle. Dabei ist für das Arrangement eigentlich nur das Json Feld
     * relevant. Beim Speichern kann der Server einige der anderen Felder aktualisieren, und genau
     * für diesen Anwendungsfall dient diese Methode, bei der kein Update des Arrangement erfolgt.
     */
    updateConfigurationWithUnchangedJson(configurationJson = null) {
        this.configuration = configurationJson;
    }

    /**
     * Serialisiert die Stage als Configuration
     */
    getStageAsConfiguration() {
        return {};
    }

    /**
     * Gibt eine Liste mit Fehlermeldungen zurück, falls das Modell nicht valide ist. Validiert eventuell lazy.
     * Eine typische Meldungen ist z.B. "Der Sicherheitsabstand zwischen den Schaukeln wird nicht eingehalten."
     */
    getStageValidationErrors() {
        return [];
    }

    getCurrentConfiguration() {
        return {};
    }

    /**
     * setzt das Autosave-Feature aktiv, d.h. sollte eine veränderte Konfiguration ungespeichert sein,
     * dann wird der Benutzer beim nächsten Öffnen des Konfigurators gefragt,
     * ob er die ungespeicherte Konfiguration weiter bearbeiten möchte. Basiert auf localStorage.
     */
    setAutosaveAndRestore(eventName = "changedConfiguration") {
        let wasStoredInThisSesssion = false;
        const deleteStoredConfiguration = () => {
            // console.log('delete localStorage.configuration');
            delete localStorage["configuration"];
            delete localStorage["configurationCamera"];
            wasStoredInThisSesssion = false;
        };
        const debouncedSave = _.debounce(() => {
            const configurationCamera = STAGE.getCurrentCameraConfig();
            if (configurationCamera)
                localStorage["configurationCamera"] = JSON.stringify(configurationCamera);

            if (APP.isConfigurationChanged()) {
                // console.log('store localStorage.configuration because ',eventName);

                localStorage["configuration"] = LZString.compress(
                    JSON.stringify(APP.getStageAsConfiguration())
                );
                wasStoredInThisSesssion = true;
            } else if (wasStoredInThisSesssion) {
                deleteStoredConfiguration();
            }
        }, 1000);

        APP.on(eventName, debouncedSave);
        APP.on("savedConfiguration", deleteStoredConfiguration);
        const deregister = () => {
            APP.off(eventName, debouncedSave);
            APP.off("savedConfiguration", deleteStoredConfiguration);
        };

        const cameraConfig =
            localStorage["configurationCamera"] == null
                ? null
                : JSON.parse(localStorage["configurationCamera"]);
        if (localStorage["configuration"] != null) {
            try {
                const storedConfiguration = JSON.parse(
                    LZString.decompress(localStorage["configuration"])
                );
                APP.onOnce("loadedInitialConfiguration", () => {
                    if (!APP.settings.autosaveAndRestore) {
                        deregister();
                        return;
                    }
                    // hier warten wir noch mal 2 Sekunden, weil einige Apps wie Zaunplaner die Konfiguration nach dem Laden
                    // noch mal aktualisieren und transiente Parameter hinzugefügt werden
                    _.delay(() => {
                        try {
                            // hier prüfen wir, ob die aktuelle Konfiguration wirklich unterschiedlich zu der aus dem localStorage ist,
                            // um den User nicht zu nerven, falls die autosave-Konfiguration gleich der geladenen ist
                            // hier interessiert nur der Json-Teil, weil der Rest reine Meta-Information ist und sich da Felder ändern können, z.B. last_access
                            let storedJson = storedConfiguration.Json;
                            let currentJson = APP.getStageAsConfiguration().Json;
                            if (!Commons.isEqualEpsilon(storedJson, currentJson)) {
                                console.log("found an unsaved localStorage.configuration");

                                const autoOK = APP.settings.userRole == "admin";
                                const restore = () => {
                                    this.setStageFromConfiguration(storedConfiguration);
                                    if (cameraConfig) {
                                        _.delay(() => {
                                            STAGE.setCameraConfig(cameraConfig);
                                        }, 500);
                                    }
                                    this._setConfigurationCookie();
                                    APP.trigger("changedConfiguration");
                                    APP.trigger("loadedConfiguration");
                                    this._setConfigurationSnapshot();
                                };

                                if (autoOK && _.isEmpty(Urls.urlParam("Code"))) {
                                    Dialogs.showToast(
                                        I18N(
                                            "Weil sie als Administrator eingeloggt sind, wurde die"
                                        ),
                                        1500
                                    );
                                    _.delay(restore, 300);
                                } else {
                                    Dialogs.askQuestion(
                                        I18N(
                                            "Wir haben eine Konfiguration im Speicher gefunden. Möchten"
                                        ),
                                        [
                                            { label: I18N("Ja"), primary: true, action: restore },
                                            {
                                                label: I18N("Nein"),
                                                action: deleteStoredConfiguration,
                                            },
                                        ]
                                    );
                                }
                            }
                        } catch (e) {
                            console.warn(e);
                        }
                    }, 2000);
                });
            } catch (e) {
                console.warn(e);
            }
        }
    }

    createWkfgBackendApi() {
        return new Wkfg2BackendApi(this.settings.wkfgBackendUrl, this.createWkfgTypeConverter());
    }

    createWkfgTypeConverter() {
        return new Wkfg2TypeConverter();
    }

    // Funktion für fotorealistisches Rendering
    // Kümmert sich um GLB-Export und Kommunikation mit dem WKFG
    // Das WKFG kommuniziert anschließend mit der ServiceAPI (zentraler neuer Server) und speichert asynchron die gerenderten Bilder
    // Im Anschluss wird die WKFG-Route "mail" aufgerufen, da die Renderings typischerweise in der Mail enthalten sind
    /**
     *
     * @param {String} projectName Projektname, sollte in als default APP.name aber gesetzt sein, und sollte mit oc_ anfangen
     * @param {String} filename Kann gesetzt werden, default reicht aber
     * @param {Object3d} object3d Ist das o3, welches Exportiert und gerendert wird. Sollte Kameras (die werden gerendert) und Lichter enthalten!
     * @param {String} code Muss den aktuellen Konfigurationscode enthalten
     * @param {Function} before Wird vor dem Export ausgeführt. Nutzbar für Dinge wie: Boxes aus o3 entfernen -> Keine Sorge, es wird mit einem deepClone gearbeitet, man manipuliert nicht die Szene auch wenn object3d = STAGE.scene
     * @param {Function} after Wird nach dem Export ausgeführt
     * @param {Boolean} hideknown Flag um bekannte Objektproblematiken automatisch zu beheben: z.B. THREE.LineSegments
     * @param {Array} instancesToBeHidden Ein Array mit Klassen-Objekten. Alle im o3 auffindbaren instanzen dieser Klassen werden im Rendering ausgeblendet
     * @param {Object} renderData Das Objekt beschreibt Rendereigenschaften die durch Blender berückstichtigt werden: Verpflichtend ist der name mit .json am Ende (sorry for that, siehe default).
     * @param {Number} multipart Ermöglicht upload von meheren GLB's: Pipeline triggert Mail ecrst nach letztem Erfolg. Hier Anzahl der gesamten GLBs angeben
     * @param {Number} part Positionsangabe innerhalb multipart
     */
    fotorealisticRender({
        projectName = APP.name,
        filename = "render.glb",
        object3d = null,
        code = null,
        before = null,
        after = null,
        hideknown = true,
        instancesToBeHidden = [],
        renderData = { name: "renderData.json" },
        multipart = 1,
        part = 1,
        useRenderfarm = false,
        blendFilename = "auto.blend",
    } = {}) {
        if (!projectName || !object3d || !code || !projectName) {
            throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
        }
        if (!projectName.includes("_")) {
            throw new Error("THE PROJECTNAME NEEDS TO INCLUDE A _ SYMBOL: e.g.: ocschrank_oc");
        }
        renderData = {hdriUrl: "", cameraCount: 1, ...renderData};
        object3d = _.cloneDeep(object3d);
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.prepareO3ForExport(object3d, hideknown, instancesToBeHidden);
                if (before) before(object3d);
                console.log(object3d.scale);
                STAGE.gltfToBlob(
                    (blob) => {
                        const renderDataJSON = {
                            fileName: renderData.name,
                            blob: new Blob([JSON.stringify(renderData)], {
                                type: "application/json",
                            }),
                        };
                        this.wkfgBackendApi
                            .uploadContent(`Konfiguration/${code}`, renderDataJSON)
                            .then(() => {
                                const glbUpload = {
                                    fileName: filename,
                                    blob: blob,
                                };
                                this.wkfgBackendApi
                                    .fotorealisticRenderAPI(
                                        `Konfiguration/${code}`,
                                        glbUpload,
                                        projectName,
                                        multipart,
                                        part,
                                        useRenderfarm,
                                        blendFilename
                                    )
                                    .then(() => {
                                        resolve();
                                    })
                                    .catch((e) => reject(e));
                            })
                            .catch((e) => reject(e));
                    },
                    { binary: true, googleARUVMapping: false },
                    object3d
                );
                if (after) after(object3d);
            })
        );
    }

    // Funktion für AR: Allerdings kein Aufruf
    // Diese Funktion kümmert sich darum, dass das WKFG die richtigen Ressourcen für AR bereitstellt
    // Wenn nötig, wird eine USDZ konvertiert und abgelegt
    // Bei jedem Aufruf wird der glb Name entsprechend manipuliert, um das Cachen des Google-Scene Viewers zu vermeiden
    /**
     * @param {String} projectName Projektname, sollte in als default APP.name aber gesetzt sein
     * @param {Boolean} ARUvMapping Sollte true sein, das UV-Mapping des GLTFExporters wird damit gefixt
     * @param {Boolean} convertToUSDZ Soll die zu speichernde GLB zu einer USDZ Konvertiert werden?
     * @param {Object3d} object3d Ist das o3, welches Exportiert und in AR Dargestellt wird.
     * @param {String} code Muss den aktuellen Konfigurationscode enthalten
     * @param {Function} before Wird vor dem Exportieren ausgeführt. Oftmals benötigt zum ausblenden unbekannter Objekte im Zusammenhang mit GLB / GLTF
     * @param {Function} after Wird nach dem Exportieren ausgeführt.
     * @param {Boolean} hideknown Flag um bekannte Objektproblematiken automatisch zu beheben: z.B. THREE.LineSegments
     * @param {Array} instancesToBeHidden Ein Array mit Klassen-Objekten. Alle im o3 auffindbaren instanzen dieser Klassen werden im Rendering ausgeblendet
     */
    augmentedReality({
        projectName = APP.name ? APP.name : null,
        ARUvMapping = null,
        convertToUSDZ = null,
        object3d = null,
        code = null,
        before = null,
        after = null,
        hideknown = true,
        instancesToBeHidden = [],
    } = {}) {
        if (!projectName || !object3d || !code) {
            throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
        }
        object3d = _.cloneDeep(object3d);
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.getNextArFileIndex(code).then((index) => {
                    this.prepareO3ForExport(object3d, hideknown, instancesToBeHidden);
                    const filename = `ar${index}.glb`;
                    if (before) before(object3d);
                    STAGE.gltfToBlob(
                        (blob) => {
                            const glbUpload = {
                                fileName: filename,
                                blob: blob,
                            };
                            this.wkfgBackendApi
                                .augmentedRealityAPI(
                                    `Konfiguration/${code}`,
                                    glbUpload,
                                    projectName,
                                    convertToUSDZ
                                )
                                .then(() => {
                                    resolve();
                                })
                                .catch((e) => reject(e));
                        },
                        { binary: true, googleARUVMapping: ARUvMapping },
                        object3d
                    );
                    if (after) after(object3d);
                });
            })
        );
    }

    // Ernittelt alle Dateien die ar enthalten und ermittelt den nächsten freien index zum anhängen
    // Zum vermeiden des Cachings durch Google bei AR
    getNextArFileIndex(code) {
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.wkfgBackendApi.listContent("Konfiguration/" + code).then((list) => {
                    let names = [];
                    list.forEach((e) => {
                        const from = e.lastIndexOf("/") + 1;
                        const to = e.lastIndexOf(".");
                        names.push(e.slice(from, to));
                    });
                    names = names.filter((n) => n.includes("ar"));
                    resolve(names.length);
                });
            })
        );
    }

    // Die Funktionen rund um AR und Rendering bieten Mechanismen um Objekte vom Export auszuschließen
    prepareO3ForExport(object3d, hideknown, instancesToBeHidden) {
        if (hideknown) {
            this.changeVisibilityForClass(THREE.LineSegments, false, object3d);
        }
        instancesToBeHidden.forEach((i) => {
            this.changeVisibilityForClass(i, false, object3d);
        });
    }

    // Helper for fotorealistic Rendering
    changeVisibilityForClass(classname, bool, object3d) {
        object3d.traverse((o3) => {
            if (o3 instanceof classname) {
                if (!bool && o3.visible) {
                    o3.visible = bool;
                } else {
                    o3.visible = bool;
                }

                if (o3.material) {
                    if (bool && o3.material.visible) {
                        o3.material.visible = bool;
                    } else {
                        o3.material.visible = bool;
                    }
                }
            }
        });
    }

    // Helper for fotorealistic Rendering
    changeVisibilityForInstance(instance, bool) {
        instance.visible = bool;
    }

    /**
     * Gibt true zurück, falls die Configuration vom Anwender verändert wurde.
     * Es wird hierbei nicht mit Dirty-Flags gearbeitet, sondern der Snapshot des zuletzt geladenen /
     * gespeicherten Stand wird mit der aktuellen Configuration verglichen.
     */
    isConfigurationChanged() {
        const now = APP._configurationSnapshot;
        const snapshot = APP.getStageAsConfiguration();

        const nowString = JSON.stringify(now);
        const snapshotString = JSON.stringify(snapshot);

        // if (nowString != snapshotString) {
        //     console.log(Commons.deepDiff(now, snapshot), now, snapshot);
        // }

        return nowString != snapshotString;
    }

    /**
     * Gibt zurück, welche "Snapshots" (Standardansichten) von der Konfiguration beim Speichern "geschossen" werden sollen.
     * @example
     * return [{
     *    // der Dateiname, die Endung '.jpg' wird angefügt
     *    name: 'Vorderansicht',
     *    // die weiteren Parameter kommen von STAGE.setCameraAndRenderConfigAndReturnUndo
     *    direction: { x: 1, y: 1, z: 1 },
     *    hideObjectsByName: ['ground','coordinateOffset','BoxRuler'],
     *    zoomTo: true,
     *    extraZoom: 0.7,
     *    backgroundColor: 0xFFFFFF,
     *    backgroundAlpha: 1,
     * }];
     */
    configurationFotoSnapshots() {
        // Hier nur als Default
        return [
            {
                name: "Vorderansicht",
                direction: { x: 0, y: 0.2, z: 1 },
                object: { x: 0, y: 0, z: 0 },
                hideObjectsByName: ["ground", "coordinateOffset"],
                zoomTo: true,
                extraZoom: 1,
                backgroundColor: 0xffffff,
                backgroundAlpha: 1,
                renderWidth: 800,
                renderHeight: 496,
                mimeType: "image/jpeg",
            },
            {
                name: "Hinteransicht",
                direction: { x: 0, y: 0.2, z: -1 },
                object: { x: 0, y: 0, z: 0 },
                hideObjectsByName: ["ground", "coordinateOffset"],
                zoomTo: true,
                extraZoom: 1,
                backgroundColor: 0xffffff,
                backgroundAlpha: 1,
                renderWidth: 800,
                renderHeight: 496,
                mimeType: "image/jpeg",
            },
        ];
    }

    /**
     * gibt die Basis-Url für Content zurück, z.B. http://localhost:9260/laravel/public/wpcontent
     */
    get contentUrl() {
        return APP.settings.wkfgContentBaseUrl.rtrim('/');
    }

    /**
     * gibt die komplette URL zu einem Content zurück,
     * also z.B. http://localhost:8080/wpcontent/Model/Helmet.glb
     */
    getContentUrl(columnMeta,value) {
        if (Commons.isEmpty(value)) {
            return null;
        }
        if (columnMeta==null) {
            throw new Error('columnMeta is null');
        }
        if (/^(http|https|file):\/\/.*$/.test(value)) {
            return value;
        }
        let url = this.contentUrl + '/';
        if (!Commons.isEmpty(columnMeta.contentPath)) {
            const contentPath = columnMeta.contentPath.ensureRight('/');
            if ((value==null || !value.startsWith('/')) && columnMeta.stripContentPath!==false) {
                url += contentPath;
            }
        }
        if (!Commons.isEmpty(value)) {
            url += value;
        }
        if (!Commons.isEmpty(columnMeta.defaultSuffix) && !/.*(\.[a-z0-9]{1,5})$/i.test(url)) {
            url += columnMeta.defaultSuffix.ensureLeft('.');
        }
        url = url.replace(/\s/g,'%20');
        return url;
    }

    /**
     * gibt einen relativen Content-URL-Pfad entsprechend einer Column-Definition zurück,
     * also z.B. Model/Helmet.glb
     * @param column
     * @param url
     * @returns {string|*}
     */
    formatContentValue(column,url) {
        if (Commons.isEmpty(url)) {
            return '';
        }
        let value = url;
        const basePath = this.contentUrl.ensureRight('/');
        if (value.startsWith(basePath)) {
            value = value.substring(basePath.length);
        }
        const contentPath = column.contentPath.ensureRight('/');
        if (column.stripContentPath!==false && !Commons.isEmpty(contentPath)) {
            value = value.substringAfter(contentPath);
        }
        if (column.stripSuffix!==false && !Commons.isEmpty(column.defaultSuffix)) {
            value = value.substringBefore(column.defaultSuffix.ensureLeft('.'));
        }
        return value;
    }

    /**
     * Gibt die URL des Standard-Bildes (FotoSnapshot) der Configuration zurück.
     */
    getConfigurationStandardImageUrl() {
        return this.getConfigurationContentUrl() + "/Foto.jpg";
    }

    /**
     * Lädt die Metadata (meist die relationalen Daten der Produkte, Materialien, etc.)
     */
    loadMetadata(query) {
        return this.wkfgBackendApi.loadMetadata(query).then((data) => {
            this.checkMetadataMustBeSet(data);
            APP.metadata = data;
            //console.log('APP.metadata=', APP.metadata);
            APP.trigger("loadedMetadata");
            return APP.metadata;
        });
    }

    /**
     * Lädt eine Configuration vom Backend. configurationQuery ist dabei der Query-URL-Teil, der an
     * das Konfiguration-Backend-API weitergeleitet wird
     */
    loadConfiguration(
        configurationQuery,
        successCallback = undefined,
        failureCallback = undefined
    ) {
        const notFound = (error) => /no record matching/g.test(error.message);

        const defaultFailureCallback = (error) => {
            if (notFound(error)) {
                Dialogs.showError(I18N("NothingFoundAtThisAdress_GoBack", APP.settings.homeUrl));
            } else {
                throw error;
            }
        };

        return this.wkfgBackendApi
            .loadConfiguration(configurationQuery)
            .catch(failureCallback || defaultFailureCallback)
            .then((configuration) => {
                // deepclone, weil der K2 z.B. beim Startup gerne die Konfiguration manuell anpasst (Regeln).
                console.log("load configuration", _.cloneDeep(configuration));
                if (!configuration) {
                    Dialogs.showError(
                        I18N("NothingFoundAtThisAdress_GoBack", APP.settings.homeUrl)
                    );
                    return;
                }
                if (successCallback) {
                    successCallback(configuration);
                }
                this._setConfigurationCookie();
                APP.trigger("changedConfiguration");
                APP.trigger("loadedConfiguration");
                this._setConfigurationSnapshot();
            });
    }

    /**
     * Prüft, ob der URL-Query-String gesetzt ist. Wenn ja, dann wird der QueryString an die Konfiguration-Backend-API
     * weitergeleitet und die entsprechende Konfiguration geladen (meist &where.Code=xxxxxxx). Sonst wird eine
     * leere Konfiguration gesetzt.
     * Sollte in den Settings ein Code gesetzt wird diese Konfiguration als Startkonfiguration genutzt.
     */
    loadInitialConfiguration() {
        // mit diesem delayed setConfigurationSnapshot wird erreicht, dass auch Aufräumarbeiten an der Configuration
        // nach dem Laden, die bei komplexen Apps wie dem Zaunplaner laufen, nicht als User-Änderungen gewertet werden
        APP.onOnce("ready", () => {
            _.delay(() => {
                this._setConfigurationSnapshot();
            }, 1000);
        });

        const code = _.isEmpty(Urls.urlParam("Code")) ? this.settings.Code : Urls.urlParam("Code");
        const id = Urls.urlParam("id");
        let searchStringStripped = "";
        if (!_.isEmpty(code)) {
            searchStringStripped = "&where.Code=" + code;
        } else if (!_.isEmpty(id)) {
            searchStringStripped = "&where.id=" + id;
        }

        if (searchStringStripped === "") {
            this.setStageFromConfiguration(null);
            this._setConfigurationCookie();
            APP.trigger("changedConfiguration");
            APP.trigger("loadedConfiguration");
            APP.trigger("loadedInitialConfiguration");
            this._setConfigurationSnapshot();
        } else {
            this.loadConfiguration(searchStringStripped, (configuration) => {
                this.setStageFromConfiguration(configuration);
                APP.trigger("loadedInitialConfiguration");
            });
        }
    }

    updatePrices(callback) {
        this.wkfgBackendApi
            .updatePrices(APP.getStageAsConfiguration())
            .then((result) => callback(result));
    }

    /**
     * Schaltet eine Warnung ein, falls die Konfiguration geändert, aber nicht gespeichert wurde, und
     * der User den Konfigurator verläßt
     */
    addOnUnloadWarning() {
        if (APP.settings.userRole == "admin") {
            return;
        }
        $(window).on("beforeunload", () => {
            if (!window.WAS_UNRECOVERABLE_ERROR && APP.isConfigurationChanged()) {
                return I18N("YouHaveUnsavedChanges");
            }
        });
    }

    /**
     * Entfernt die UnloadWarning wieder
     */
    removeOnUnloadWarning() {
        $(window).off("beforeunload");
    }

    /**
     * Prüft, ob Metadaten existieren. Eine leere Metadaten-DB ist ein Fehler. Falls man das nicht will, hier
     * überschreiben.
     */
    checkMetadataMustBeSet(data) {
        if (_.isEmpty(data) || _.every(_.values(data), (x) => _.isEmpty(x))) {
            console.error("Metadata is empty! result from server was: ", data);
            // throw exception("ServerCommunicationFailure", I18N('ProductDbIsEmpty'));
        }
    }

    /**
     * Setzt einen Domain-weiten Cookie
     */
    setCookie(cookie_name, value) {
        Cookies.setItem(cookie_name, value, undefined, "/");
        // console.log("set " + cookie_name + "='" + Cookies.getItem(cookie_name) + "'");
    }

    /**
     * Gibt die Url auf das Content-Verzeichnis zur Configuration zurück
     */
    getConfigurationContentUrl() {
        return (
            APP.settings.wkfgContentBaseUrl + "Konfiguration/" + this.getCurrentConfiguration().Code
        );
    }

    /**
     * Teilt die Configuration auf Facebook. Die Konfiguration wird erst gespeichert, und entsprechende Dialoge öffnen sich.
     */
    shareFacebook() {
        const myURL = APP.settings.configuratorUrl + "?Code=" + this.getCurrentConfiguration().Code;
        this._shareSocial(
            "Facebook",
            APP.settings.pleaseWaitFacebookUrl,
            "http://www.facebook.com/sharer/sharer.php?u=" +
                encodeURIComponent(myURL) +
                "&t=" +
                I18N("App-Name")
        );
    }

    /**
     * Teilt die Configuration auf Pinterest. Die Konfiguration wird erst gespeichert, und entsprechende Dialoge öffnen sich.
     */
    sharePinterest() {
        const myURL = APP.settings.configuratorUrl + "?Code=" + this.getCurrentConfiguration().Code;
        const myImg = this.getConfigurationStandardImageUrl();
        this._shareSocial(
            "Pinterest",
            APP.settings.pleaseWaitPinterestUrl,
            "http://pinterest.com/pin/create/button/?url=" +
                encodeURIComponent(myURL) +
                "&media=" +
                encodeURIComponent(myImg) +
                "&description=" +
                I18N("App-Name")
        );
    }

    /**
     * Speichert die Konfiguration und erzeugt ein PDF, das heruntergeladen bzw. im Browser geöffnet wird.
     */
    pdf(callback = null, options = {}) {
        if (this._isValidationErrorAndMaybeShowError(I18N("ausgedruckt"))) {
            return;
        }

        STAGE.setRenderOffline(true);

        // const waitingDialog = Dialogs.showWaiting(I18N("das PDF wird erzeugt..."));

        options = Commons.defaultsDeepSafe({}, options, {
            callback: (configuration) => {
                STAGE.setRenderOffline(false);

                // füge Url-Parameter der Location und options.additionalParameter an die PDF-Url an
                const pdfUrlFluid = new UrlFluid(
                    this.wkfgBackendApi.getPdfUrl(configuration.Code, false)
                );
                Urls.getParameters()
                    .filter((pair) => /tenant|lang/.test(pair[0]))
                    .forEach((pair) => {
                        pdfUrlFluid.param(pair[0], pair[1]);
                    });
                if (!Commons.isEmpty(options.additionalParameter)) {
                    Object.keys(options.additionalParameter).forEach((k) => {
                        pdfUrlFluid.param(k, options.additionalParameter[k]);
                    });
                }
                let pdfUrl = pdfUrlFluid.toString();
                console.log("pdf wird erzeugt: ", pdfUrl);

                if (DeviceDetection.isDesktop()) {
                    const iframe = $(
                        '<iframe id="printIFrame" width="100px" height="100px" src="' +
                            pdfUrl +
                            '"/>'
                    );
                    $("body").append(iframe);
                    _.delay(() => {
                        this._setConfigurationCookie();
                        // waitingDialog.hide();
                        // Dialogs.showToast(I18N("das PDF wurde heruntergeladen"), 5000);
                        _.delay(() => iframe.remove(), 10000);
                    }, 2000);
                } else {
                    // waitingDialog.hide();
                    // Dialogs.showOk(
                    //     I18N(
                    //         "Sie können das PDF über folgenden Link öffnen: <br/><br/> <a",
                    //         pdfUrl,
                    //         pdfUrl
                    //     )
                    // );
                }
                if (callback) callback();
            },
            onCancel: () => {
                // waitingDialog.hide();
                STAGE.setRenderOffline(false);
            },
            preferUpdate: true,
            quiet: true,
        });
        if (this.configuration.Locked && !this.isConfigurationChanged()) {
            options.callback(this.configuration);
        } else {
            this.save(options);
        }
    }

    /**
     * Speichert die Konfiguration.
     * @param options siehe Code
     */
    save(options) {
        options = Commons.defaultsDeepSafe({}, options, {
            // wird bei Erfolg aufgerufen
            callback: (configuration) => {},
            // Wird innerhalb des blurs aufgerufen, muss cb aufrufen
            inSaveCallback: (cb) => cb(),
            // falls die Wahl zwischen Update oder Create besteht, soll automatisch update genommen werden?
            preferUpdate: false,
            // Hiermit kann ein create erzwungen werden
            // Wenn preferUpdate: false und forceNoUpdate wird ein create erzwungen
            forceNoUpdate: false,
            // sollen keine Meldungen ausgegeben werden?
            quiet: false,
            // sollen keine Bilder generiert werden?
            noFotoSnapshots: false,
            // callback, der vor dem Speichern aufgerufen wird (in method steht 'create' oder 'update')
            modifyBeforeWrite: (configuration, method) => {},
            onCancel: () => {},
        });

        const configuration = APP.getStageAsConfiguration();

        if (
            this._isValidationErrorAndMaybeShowError(
                "CantSaveTheConfigurationBecauseOfTheFollowingErrors"
            )
        ) {
            return;
        }

        const create = "create";
        const update = "update";
        const create_or_update = "create_or_update";

        const decision_table = {
            admin: {
                default_config: create,
                inspiration_config: create_or_update,
                admin_config: create_or_update,
                customer_config: create_or_update,
                locked_config: create_or_update,
            },
            haendler: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create_or_update,
            },
            kunde_eingeloggt: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create,
            },
            kunde_nicht_eingeloggt: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create,
            },
        };

        const role = APP.settings.userRole;

        const configurationType = this.getConfigurationType(configuration);
        let decision = decision_table[role][configurationType];

        if (options.preferUpdate && decision == create_or_update) {
            decision = update;
        } else if (!options.preferUpdate && options.forceNoUpdate) {
            decision = create;
        }

        // console.info('save decision=' + decision + ', role=' + role + ' configurationType=' + configurationType);

        if (configurationType == "locked_config") {
            if (decision == create) {
                Dialogs.askQuestion(I18N("LockedCreateANewConfiguration"), [
                    {
                        label: I18N("CreateNew"),
                        primary: true,
                        action: () => {
                            configuration.Locked = false;
                            this._saveAction(
                                configuration,
                                create,
                                options.callback,
                                options.quiet,
                                options.noFotoSnapshots,
                                options.modifyBeforeWrite,
                                options.inSaveCallback,
                                options.skipURLUpdate
                            );
                        },
                    },
                    {
                        label: I18N("Cancel"),
                        action: () => {
                            options.onCancel();
                        },
                    },
                ]);
            } else {
                //decission == create_or_update || update;
                Dialogs.askQuestion(I18N("LockedOverrideOrCreateNew"), [
                    {
                        label: I18N("CreateNew"),
                        primary: true,
                        action: () => {
                            configuration.Locked = false;
                            this._saveAction(
                                configuration,
                                create,
                                options.callback,
                                options.quiet,
                                options.noFotoSnapshots,
                                options.modifyBeforeWrite,
                                options.inSaveCallback,
                                options.skipURLUpdate
                            );
                        },
                    },
                    {
                        label: I18N("Overwrite"),
                        action: () =>
                            this._saveAction(
                                configuration,
                                update,
                                options.callback,
                                options.quiet,
                                options.noFotoSnapshots,
                                options.modifyBeforeWrite,
                                options.inSaveCallback,
                                options.skipURLUpdate
                            ),
                    },
                    {
                        label: I18N("Cancel"),
                        action: () => {
                            options.onCancel();
                        },
                    },
                ]);
            }
        } else if (decision == create_or_update) {
            Dialogs.askQuestion(I18N("WouldYouLikeToReplaceOrAddTheConfiguration"), [
                {
                    label: I18N("Overwrite"),
                    primary: true,
                    action: () =>
                        this._saveAction(
                            configuration,
                            update,
                            options.callback,
                            options.quiet,
                            options.noFotoSnapshots,
                            options.modifyBeforeWrite,
                            options.inSaveCallback,
                            options.skipURLUpdate
                        ),
                },
                {
                    label: I18N("CreateNew"),
                    action: () =>
                        this._saveAction(
                            configuration,
                            create,
                            options.callback,
                            options.quiet,
                            options.noFotoSnapshots,
                            options.modifyBeforeWrite,
                            options.inSaveCallback,
                            options.skipURLUpdate
                        ),
                } /*, {
                label: I18N('Cancel'),
                action: () => {
                }
            }*/,
            ]);
        } else {
            this._saveAction(
                configuration,
                decision,
                options.callback,
                options.quiet,
                options.noFotoSnapshots,
                options.modifyBeforeWrite,
                options.inSaveCallback,
                options.skipURLUpdate
            );
        }
    }

    /**
     * ermittelt die Art der Konfiguration, kann sein:
     * - 'default_config' die Konfiguration wurde nicht geladen sondern entspricht dem programmatischen Default / "Leer"
     * - 'inspiration_config' die Konfiguration ist eine Inspiration (Inspiration=true) und wurde vom Händler erstellt
     * - 'customer_config' die Konfiguration wurde vom Kunden erstellt
     * - 'admin_config' die Konfiguration wurde vom Admin erstellt
     */
    getConfigurationType(configuration) {
        if (typeof configuration.id === "undefined") {
            return "default_config";
        } else if (configuration.Inspiration) {
            return "inspiration_config";
        } else if (configuration.Locked) {
            return "locked_config";
        } else if (configuration.UserGuid == APP.settings.userGuid) {
            return "customer_config";
        } else {
            return "admin_config";
        }
    }

    /**
     * Lädt die Beschreibung des Metamodells und der Metadaten in einem, und gibt ein Promise zurück
     * Hinweis: Best Practise ist es, die I18N-Texte nicht über diesen Weg, sondern direkt als Teil der HTML-Seite auszuliefern,
     * damit z.B. der Loading-Screen oder Fehlermeldungen in der richtigen Sprache erscheinen.
     * @returns {Promise} {
     *    Konfiguration: {
     *        id: 1,
     *        Code: "MBN443QTX",
     *        ...
     *    },
     *    metadata: {
     *        MaterialPbr: [
     *          {
     *              id: 1,
     *              name: "Leder",
     *              ...
     *          },
     *          ...
     *        ]
     *    },
     *    metamodell: {
     *       languagesI18N: ["de","en"],
     *       tables: [
     *          {
     *              name: "Konfiguration",
     *              label: "Konfigurationen",
     *              ...
     *          },
     *          ...
     *       }
     *    },
     *    i18n_json: {
     *       "__shortname": "de",
     *       "number_dec_point": ",",
     *       "3 pro Seite": "3 pro Seite",
     *       ...
     *    },
     *    content_url: 'http://localhost/wp-content/uploads/wkfgcontent',
     * }
     */
    loadConfigData(options) {
        return this.wkfgBackendApi.loadConfigData(options).then((data) => {
            APP.metamodel = data.metamodel;
            APP.metadata = data.metadata;
            APP.configuration = _.get(data, "Konfiguration[0]");
        });
    }

    loadInitialConfigurationFromApp() {
        APP.trigger("loadedMetamodel");
        const initialConfiguration = APP.configuration;
        this.setStageFromConfiguration(initialConfiguration);
        if (initialConfiguration == null) {
            this._setConfigurationCookie();
            APP.trigger("changedConfiguration");
            APP.trigger("loadedConfiguration");
            APP.trigger("loadedInitialConfiguration");
            this._setConfigurationSnapshot();
        } else {
            APP.trigger("loadedInitialConfiguration");
        }
    }

    /**
     * Lädt die Beschreibung des Metamodells - also den Inhalt der metamodel.json
     * @param callback
     */
    loadMetamodel(callback = (metamodell) => {}) {
        return this.wkfgBackendApi.loadMetamodel().then((data) => {
            APP.metamodel = data;
            callback(APP.metamodel);
            APP.trigger("loadedMetamodel");
            return APP.metamodel;
        });
    }

    /**
     * Löscht eine Konfiguration mitsamt Content
     * @param id
     * @param callback
     */
    deleteConfiguration(id, callback = () => {}) {
        return this.wkfgBackendApi.deleteMetadata("Konfiguration", id).then(callback);
    }

    /**
     * @private
     */
    _saveAction(
        configuration,
        method,
        callback = (configuration) => {},
        quiet = false,
        noFotoSnapshots = false,
        modifyBeforeWrite = () => {},
        inSaveCallback = (cb) => cb(),
        skipURLUpdate = false
    ) {
        const saveIntern = () => {
            let freeze = $("<div>");
            let waitingDialog = $("<div>");

            if (!quiet) {
                waitingDialog = Dialogs.showWaiting(I18N("saving..."));
            }

            if (!noFotoSnapshots) {
                freeze = STAGE.freezeRendererAndBlur();
                STAGE.stopAnimation = true;
                if (APP.representer) {
                    APP.representer.disabled = true;
                }
            }
            _.delay(
                () => {
                    configuration.UserGuid = APP.settings.userGuid;
                    configuration.camera = STAGE.getCurrentCameraConfig();

                    modifyBeforeWrite(configuration, method);

                    //console.log('configuration before save ', configuration);
                    this.wkfgBackendApi
                        .saveMetadata("Konfiguration", method, configuration)
                        .then((configurationFromServer) => {
                            const setBrowserUrlWithoutReload = () => {
                                const configuration = APP.getStageAsConfiguration();
                                const code = configuration.code
                                    ? configuration.code()
                                    : configuration.Code;
                                let configuratorUrl = Urls.addOrUpdateQsParameter(
                                    window.location.href,
                                    "Code",
                                    code
                                );
                                if (!Commons.isEmpty(code) && !Commons.isEmpty(configuratorUrl)) {
                                    // Unter FF kann ein NS_ERROR_ILLEGAL_VALUE aufreten der nicht gecached werden
                                    // kann wenn der String > 640 kb ist, daher compress
                                    history.pushState(null, "", configuratorUrl);
                                }
                            };

                            console.log("configurationFromServer", configurationFromServer);

                            // transactional: erst jetzt, wo der Server die Config wirklich gespeichert hat, daten übertragen
                            this.updateConfigurationWithUnchangedJson(configurationFromServer);

                            if(!skipURLUpdate) setBrowserUrlWithoutReload();

                            // Sketches: Grafiken, die aus einem Canvas erzeugt werden.
                            // APP.uploadSketches sollte ein Array mit Objekten der Form {name: String, canvasPromise: Promise}
                            // zurückgeben. Das canvasPromise selbst löst zu einem Canvas-Objekt auf.
                            if (this.uploadSketches) {
                                const sketchDatas = this.uploadSketches()?.filter(
                                    (sketchData) => sketchData?.canvasPromise != null
                                );
                                const sketchPromises = sketchDatas.map(
                                    (sketchData) => sketchData.canvasPromise
                                );
                                Promise.all(sketchPromises).then((canvases) => {
                                    Promise.all(
                                        canvases.map(
                                            (canvas) =>
                                                new Promise((resolve) => canvas.toBlob(resolve))
                                        )
                                    ).then((canvasBlobs) => {
                                        const filesArray = canvasBlobs.map((canvasBlob, index) => {
                                            return {
                                                fileName: sketchDatas[index].name,
                                                blob: canvasBlob,
                                            };
                                        });
                                        this.wkfgBackendApi
                                            .uploadContent(
                                                "Konfiguration/" + configurationFromServer.Code,
                                                filesArray
                                            )
                                            .then(
                                                () => {},
                                                () => {
                                                    console.log(
                                                        "Sketches upload fehlgeschlagen",
                                                        sketchDatas
                                                    );
                                                }
                                            );
                                    });
                                });
                            }

                            const appendFileNameAndBlobForUpload = (
                                options,
                                fileNameAndBlobsArray,
                                uploadCallback
                            ) => {
                                if (APP.imageStack) {
                                    //2D Konfigurator
                                    _.delay(
                                        () => {
                                            if(options.beforeScreenshotCallback) {
                                                options.beforeScreenshotCallback();
                                            }

                                            STAGE2D.setRendererToSize(
                                                options.renderWidth,
                                                options.renderHeight
                                            );
                                            const undo = () => STAGE2D.undo();
                                            STAGE2D.renderToBlob(options.mimeType, (blob) => {
                                                undo();

                                                if(options.afterScreenshotCallback) {
                                                    options.afterScreenshotCallback();
                                                }

                                                const formData = new FormData();
                                                const suffix =
                                                    "." +
                                                    Mimetypes.getSuffixOfMimetype(options.mimeType);

                                                fileNameAndBlobsArray.push({
                                                    fileName: options.name + suffix,
                                                    blob: blob,
                                                });
                                                uploadCallback();
                                            });
                                        },
                                        options.timeBeforeRender != null
                                            ? options.timeBeforeRender
                                            : 1000
                                    );
                                } else if (options.glb === true) {
                                    STAGE.gltfToBlob((blob) => {
                                        fileNameAndBlobsArray.push({
                                            fileName: options.name + ".glb",
                                            blob: blob,
                                        });
                                        uploadCallback();
                                    });
                                } else {
                                    if(options.beforeScreenshotCallback) {
                                        options.beforeScreenshotCallback();
                                    }
                                    // console.log('appendFileNameAndBlobForUpload',options);
                                    if(options.beforeScreenshotCallback) {
                                        options.beforeScreenshotCallback();
                                    }
                                    STAGE.setRendererToSize(
                                        options.renderWidth,
                                        options.renderHeight
                                    );
                                    const undo = STAGE.setCameraAndRenderConfigAndReturnUndo(
                                        options
                                    );

                                    _.delay(
                                        () => {
                                            STAGE.renderToBlob(options.mimeType, (blob) => {
                                                undo();
                                                if(options.afterScreenshotCallback) {
                                                    options.afterScreenshotCallback();
                                                }

                                                const formData = new FormData();
                                                const suffix =
                                                    "." +
                                                    Mimetypes.getSuffixOfMimetype(options.mimeType);

                                                fileNameAndBlobsArray.push({
                                                    fileName: options.name + suffix,
                                                    blob: blob,
                                                });
                                                uploadCallback();
                                            });
                                        },
                                        options.timeBeforeRender != null
                                            ? options.timeBeforeRender
                                            : 1000
                                    );
                                }
                            };

                            if (!noFotoSnapshots) {
                                const fileNameAndBlobsArray = [];
                                const snapshots = _.map(
                                    _.clone(this.configurationFotoSnapshots()),
                                    (x) => {
                                        return _.curry(appendFileNameAndBlobForUpload)(
                                            x,
                                            fileNameAndBlobsArray
                                        );
                                    }
                                );

                                const callSnaphots = (snapshots) => {
                                    if (snapshots.length > 0) {
                                        const fn = _.head(snapshots);
                                        fn(_.partial(callSnaphots, _.slice(snapshots, 1)));
                                    } else {
                                        this.wkfgBackendApi.uploadContent(
                                            "Konfiguration/" + configurationFromServer.Code,
                                            fileNameAndBlobsArray
                                        );
                                        STAGE.setRendererToSize();
                                        STAGE.stopAnimation = false;
                                        if (APP.representer) {
                                            APP.representer.disabled = false;
                                        }
                                        APP.trigger("savedConfiguration");
                                        this._setConfigurationCookie();
                                        this._setConfigurationSnapshot();
                                        const waitingTimeForServerProcessing =
                                            1000 + fileNameAndBlobsArray.length * 300;
                                        _.delay(() => {
                                            if (!quiet) {
                                                APP.showSavedDialog(configurationFromServer);
                                            }
                                            inSaveCallback(() => {
                                                waitingDialog.hide();
                                                freeze.hide();
                                                callback(configurationFromServer);
                                            });
                                        }, waitingTimeForServerProcessing);
                                    }
                                };
                                callSnaphots(snapshots);
                            } else {
                                _.delay(
                                    () => {
                                        APP.trigger("savedConfiguration");
                                        this._setConfigurationCookie();
                                        this._setConfigurationSnapshot();
                                        if (!quiet) {
                                            APP.showSavedDialog(configurationFromServer);
                                            waitingDialog.hide();
                                        }
                                        inSaveCallback(() => {
                                            callback(configurationFromServer);
                                        });
                                    },
                                    this.uploadSketches ? 2000 : 0
                                );
                            }
                        });
                },
                noFotoSnapshots ? 0 : 500
            );
        };

        configuration.UserId = APP.settings.userId;

        if (method == "create") {
            configuration.id = null;
            configuration.Code = null;
        }

        if (APP.settings.userRole != "admin") {
            configuration.Inspiration = false;
        }

        modifyBeforeWrite(configuration, method);
        APP.trigger("beforeSaveConfiguration", configuration, method);

        saveIntern();
    }

    showSavedDialog(configurationFromServer) {
        Dialogs.showOk(HBS("oc-app/configurationSafedMessage.hbs")(configurationFromServer));
    }

    /**
     * @private
     */
    _isValidationErrorAndMaybeShowError(errorMessage) {
        const validationErrors = this.getStageValidationErrors();
        if (_.isEmpty(validationErrors)) {
            return false;
        }
        if (I18N) {
            errorMessage = I18N(errorMessage);
        }
        Dialogs.showWarning(errorMessage + " <br/> " + validationErrors.join("<br/>"));
        return true;
    }

    /**
     * @private
     */
    _shareSocial(socialNetworkName, pleaseWaitUrl, shareUrl) {
        const width = 800;
        const height = 600;
        const win = window.open(
            pleaseWaitUrl,
            socialNetworkName,
            "toolbar=0, status=0, width=" + width + ", height=" + height
        );
        this.save({
            preferUpdate: true,
            quiet: true,
            callback: function () {
                win.location.href = shareUrl;
            },
        });
    }

    /**
     * @private
     */
    _setConfigurationCookie() {
        const configuration = this.getCurrentConfiguration();
        if (configuration?.Code) {
            this.setCookie(
                "holodeck_configuration_code_cookie",
                this.getCurrentConfiguration().Code
            );
        }
    }

    /**
     * @private
     */
    _setConfigurationSnapshot() {
        //console.log('setConfigurationSnapshot');
        APP._configurationSnapshot = Commons.cloneDeep(this.getStageAsConfiguration());
    }

    get wkfgBackendApi() {
        if (this._wkfgBackendApi == null) {
            this._wkfgBackendApi = new Wkfg2BackendApi(this.settings.wkfgBackendUrl);
        }
        return this._wkfgBackendApi;
    }

    set wkfgBackendApi(value) {
        this._wkfgBackendApi = value;
    }

    get modelsHelper() {
        if (this._modelsHelper == null) {
            this._modelsHelper = new ModelsHelper();
        }
        return this._modelsHelper;
    }

    set modelsHelper(value) {
        this._modelsHelper = value;
    }

}
